import { inject } from '@angular/core';
import {
  Router,
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { UserRole } from '../models/role.enum';

/**
 * Role-based Authorization Guard
 * Protects routes based on user roles
 * Redirects to unauthorized page if user doesn't have required role
 */
export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // First check if user is authenticated
  if (!authService.isUserAuthenticated()) {
    router.navigate(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  // Get required roles from route data
  const requiredRoles = route.data['roles'] as UserRole[];

  if (!requiredRoles || requiredRoles.length === 0) {
    // No specific roles required, just authentication
    return true;
  }

  // Check if user has any of the required roles
  if (authService.hasAnyRole(requiredRoles)) {
    return true;
  }

  // User doesn't have required role - show access denied screen
  console.warn('Access denied: User does not have required role');
  return router.createUrlTree(['/unauthorized']);
};

/**
 * Factory function to create role guard with specific roles
 * Usage: canActivate: [createRoleGuard([UserRole.ADMIN, UserRole.ORGANIZATION])]
 */
export function createRoleGuard(allowedRoles: UserRole[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);
  
    if (!authService.isUserAuthenticated()) {
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    if (authService.hasAnyRole(allowedRoles)) {
      return true;
    }

    // User doesn't have required role - show access denied screen
    console.warn('Access denied: User does not have required role');
    return router.createUrlTree(['/unauthorized']);
  };
}
